// Angular
import {
	Component, ComponentFactoryResolver,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Service
import { SmartTagsService } from '../services/smart-tags.service';
import { BroadcastEventModel, BroadcastService, ModalService, ModalSize, ObjectUtils } from '@cv-invision/core';
import { ViewsService } from '../../../../../../services/views.service';
import { LayoutRenderService } from '../../../../services/layout-render.service';
// Model
import { SmartTagItem } from '../model/smart-tags.model';
import { SelectPhotoResponse } from '../../../../../../models/views-service.model';
import { FORM_EVENT, PHOTO_SELECT_STATUS } from '../../../../../../models/enums.model';
import { SmartTagsEventType, RMCEventType, TabsEventType } from '../../../models/component.events';
import { FormModelType, ResourceMediaModel } from '../../resource-media-control/model/resource-media.model';
import { CaptureType } from '../../panorama-tour/model/report-image.model';
import { Lightbox } from 'ngx-lightbox';
import { RotateImageComponent } from "../../rotate-image/rotate-image.component";
import { DrawFormsComponent } from "../../draw-forms/draw-forms.component";
import { CVfUtilsService } from 'libs/cv.invision-views.ux/src/lib/open-schema/services/cvf-utils.service';

@Component({
	selector: 'cv-schema-render-smart-tag-item',
	templateUrl: './smart-tag-item.component.html'
})
export class SmartTagItemComponent implements OnInit, OnDestroy {
	@ViewChild('smartTagItem') smartTagItemContainer: ElementRef<HTMLElement>;

	@Input() item: SmartTagItem;
	@Input() path: string;
	@Input() version: string;
	@Input() external: boolean;

	@Output() edit: EventEmitter<any> = new EventEmitter<any>();

	album: any = [];
	previewPhoto: any = null;
	showLoading = true;
	selected: boolean;
	showFullDescription = false;
	displayShowMoreButton = false;
	photoIsAvailable = true;
	retriesLoadImage = 0;
	canRotateBlur = false;

	private atComponentDestroy = new Subject();
	private interval: number;
	private groupsHandledInSameSection = ['LOCATION', 'ENERGY'];

	get photosSelected(): any {
		return this.viewsService.photosSelected;
	}

	get isEditing(): boolean {
		return this.layoutRenderService.activateEditableUX;
	}
	get isAIgenerated(): boolean {
		return ('confidence' in this.item.data && this.item.data.confidence > 0);
	}

	get confidence(): number {
		let response = 0;

		if (this.isAIgenerated) {
			response = Math.round(this.item.data.confidence);
		}

		return response;
	}

	constructor(
		private broadcastService: BroadcastService,
		private modalService: ModalService,
		private viewsService: ViewsService,
		private layoutRenderService: LayoutRenderService,
		private smartTagsService: SmartTagsService,
		private lightbox: Lightbox,
		private componentFactoryResolver: ComponentFactoryResolver,
		private cvfUtils: CVfUtilsService
	) {
		this.broadcastService
			.onEvent(FORM_EVENT.PHOTO_SELECT_ALL_NONE)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				this.selectPhoto();
			});

		this.broadcastService
			.onEvent(FORM_EVENT.PHOTO_SELECT)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				if ('jsonPath' in broadcast.event && broadcast.event.jsonPath === this.path) {
					const photoId = broadcast.event.data.id;
					const index = this.album.findIndex((img: any) => img.id === photoId);

					if (index > -1) {
						this.updateAlbum(broadcast.event.data);
					}
				}
			});
	}

	ngOnInit(): void {
		this.selected = false;
		this.loadImages();
		if (this.item.photos.length) {
			this.previewPhoto = this.item.photos[0]?.thumbnails[0] || null;
		} else {
			this.photoIsAvailable = false;
			this.showLoading = false;
		}
		this.canRotateBlur = this.getRequiredPhoto();
	}

	/**
	 * event to load images into album
	 */
	loadImages(): void {
		this.album = [];
		this.item.photos.forEach(value => {
			this.addImageToAlbum(value);
		});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();

		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	/**
	 * Callback after image is loaded
	 */
	onImageLoad(): void {
		this.showLoading = false;
		if (this.interval) {
			clearInterval(this.interval);
		}
		const imageHeight = this.smartTagItemContainer.nativeElement.querySelector('.smart-tag-image').clientHeight;
		const smartTagInfoHeight = this.smartTagItemContainer.nativeElement.querySelector('.smart-tag-description').scrollHeight;
		this.displayShowMoreButton = imageHeight < smartTagInfoHeight + 20
	}

	onImageError(): void {
		this.previewPhoto = null;
		if (this.interval) {
			clearInterval(this.interval);
		}

		this.showLoading = true;
		this.interval = window.setInterval(() => {
			this.previewPhoto = this.item.photos[0]?.thumbnails[0];
			this.retriesLoadImage++;

			if (this.retriesLoadImage >= 10) {
				this.photoIsAvailable = false;
				this.showLoading = false;
				clearInterval(this.interval);
			}
		}, 1000);
	}

	/**
	 * event to delete a selected smart tag
	 */
	deleteSmartTag(): void {
		const photoSelected = this.album[0];
		this.modalService
			.customConfirm(
				'Delete Smart Tag',
				`Do you really want to delete it? There is no undo operation`,
				'icon-warning fas fa-exclamation-circle',
				'Delete',
				'cv-btn-link-error'
			)
			.subscribe(async result => {
				if (result.confirm === 'confirm') {
					this.smartTagsService.smartTagDataChanged.next(
						{
							type: SmartTagsEventType.DELETE_SMART_TAG,
							path: this.path,
							data: this.item
						}
					);
				}
			});
	}

	/**
	 * Invoke the Edit of Smart Tags
	 */
	public editSmartTag(): void {
		const resourceMediaModel: ResourceMediaModel = new ResourceMediaModel();
		// Prepare the Smart Tag Data
		const smartTagData = ObjectUtils.recursiveDeepCopy(this.item.data);
		const smartTagGroupName = (smartTagData.groupName || '').toUpperCase();
		delete smartTagData.renderView;

		resourceMediaModel.snapshot = {
			preview: true,
			url: this.item.photos[0]?.thumbnails[0]
		};
		resourceMediaModel.isImageless = smartTagData.imageless;
		resourceMediaModel.captureType = CaptureType.SMARTTAG;
		resourceMediaModel.formModelType = FormModelType.EDIT;
		const metaData = {
			resourceMediaModel: resourceMediaModel,
			smartTagData: smartTagData
		};
		// Keep internal
		if (this.external
			&& smartTagData
			&& this.groupsHandledInSameSection.indexOf(smartTagGroupName) > -1
		) {
			this.edit.next(metaData);
		} else {
			// Open the Resource Media Control Panel
			this.layoutRenderService.componentBroadcast.next({
				eventType: RMCEventType.OPEN,
				jsonPath: this.path,
				metaData: metaData
			});
		}
	}

	/**
	 * open preview to display image in full size
	 */
	openPreview(): void {
		this.lightbox.open(this.album, 0);
	}

	/**
	 * Broadcast to select all or none photos
	 */
	selectPhoto() {
		const photo = this.album[0];
		let photoindex = -1;

		if (photo) {
			photoindex = this.photosSelected.indexOf(photo.id);
		}
		photoindex > -1 ? this.selected = true : this.selected = false
	}

	/**
	 * Select a smartag in the list
	 */
	selectSmartTag(triggerBroadcast?: boolean) {
		const photoSelected = this.album[0];
		const photoStatus = this.viewsService.selectPhoto(photoSelected.id);

		this.updateAlbum(photoStatus);

		if (triggerBroadcast) {
			this.broadcastService.broadcast({
				name: FORM_EVENT.PHOTO_SELECT,
				event: {
					data: photoStatus,
					jsonPath: this.path
				}
			});
		}
	}

	private updateAlbum(photoStatus: SelectPhotoResponse): void {
		this.selected = false;

		if (photoStatus.status === PHOTO_SELECT_STATUS.SELECTED) {
			this.selected = true;
		}
	}

	/**
	 * Add image object to class variable album
	 * @param img
	 */
	private addImageToAlbum(img: any): void {
		if (img) {

			const image = {
				id: img.id,
				src: img.path,
				big: img.path,
				small: img.thumbnails[0],
				medium: img.thumbnails[0],
				thumb: img.thumbnails[0],
				description: '',
				showDelete: img.custom,
				refJsonPath: img.refJsonPath
			};
			this.album.push(image);
		}

		this.selectPhoto();
	}

	/**
	 * event to open Modal to rotate image
	 */
	rotateImage(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: RotateImageComponent,
				data: {
					jsonPath: this.path,
					currentAlbumImage: this.album[0],
					photos: this.item.photos,
					order: this.viewsService.order
				},
				modalConfiguration: {
					title: `Rotate Image`,
					modalSize: ModalSize.LG
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result.id) {
					const photoSelectedIndex = this.item.photos.findIndex(item => item.id === result.id);
					if (photoSelectedIndex >= 0) {
						const newPhotoAlbum = ObjectUtils.recursiveDeepCopy(this.item.photos[photoSelectedIndex])
						newPhotoAlbum.path = result.path;
						newPhotoAlbum.thumbnails = result.thumbnails;
						newPhotoAlbum['forceReload'] = Date.now();
						this.item.photos.splice(photoSelectedIndex, 1, newPhotoAlbum);
						this.previewPhoto = this.item.photos[0]?.thumbnails[0] || null;
						this.loadImages();
					}

				}
			});
	}

	/**
	 * Event to open modal to draw forms in images for smart tags
	 * @private
	 */
	openDrawForms(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: DrawFormsComponent,
				data: {
					jsonPath: this.path,
					currentAlbumImage: this.album[0],
					photos: this.item.photos,
					order: this.viewsService.order
				},
				modalConfiguration: {
					modalCustomClass: 'blur-modal',
					title: `Apply Blur`,
					modalSize: ModalSize.XXL,
					titleIcon: 'far fa-pen-paintbrush'
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (result) => {
				if (result.id) {
					const photoSelectedIndex = this.item.photos.findIndex(item => item.id === result.id);
					if (photoSelectedIndex >= 0) {
						const newPhotoAlbum = ObjectUtils.recursiveDeepCopy(this.item.photos[photoSelectedIndex])
						newPhotoAlbum.path = result.path;
						newPhotoAlbum.thumbnails = result.thumbnails;
						newPhotoAlbum['forceReload'] = Date.now();
						this.item.photos.splice(photoSelectedIndex, 1, newPhotoAlbum);
						this.previewPhoto = this.item.photos[0]?.thumbnails[0] || null;
						await this.loadImages();
					}

				}
			});
	}

	/**
	 * Get the requiredPhoto from the smartTag config Data
	 *
	 * @returns
	 */
	getRequiredPhoto(): boolean {
		const smartTagTypeList = ObjectUtils.recursiveDeepCopy(this.layoutRenderService.getExtraDataOption('smartTagTypes') || []);
		const smartTagItem = this.cvfUtils.getSmartTagItem(smartTagTypeList, this.item.data.tagType);
		const indexIsRequiredPhoto = smartTagItem?.smartTagType?.formControl?.findIndex(item => item.field === 'requiredPhoto');

		if(!indexIsRequiredPhoto || indexIsRequiredPhoto < 0){
			return true;
		}

		return this.item.data.requiredPhoto;
	}
}
